import { type ServiceParams, unwrapJson } from "../client";
import type {
  LoginRequest,
  LoginResponse,
  MeResponse,
  RefreshRequest,
  RefreshResponse,
  RegisterRequest,
  RegisterResponse,
  SwitchWorkspaceRequest,
  ValidateSignupKeyResponse,
} from "./types";

/**
 * SAC-pattern service factory. The factory captures the base URL +
 * header configurator from :class:`ServiceParams` so every call
 * site uses the same baseline. Imports from
 * :mod:`@/api/client.ts`'s ``baseParams`` should be the only
 * consumers of this factory outside of test seams.
 */
const Auth = ({ url, config }: ServiceParams) => ({
  async login(body: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(`${url}/v1/auth/login`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<LoginResponse>(response);
  },

  async register(body: RegisterRequest): Promise<RegisterResponse> {
    const response = await fetch(`${url}/v1/auth/register`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<RegisterResponse>(response);
  },

  async validateSignupKey(code: string): Promise<ValidateSignupKeyResponse> {
    const response = await fetch(
      `${url}/v1/auth/signup-keys/${encodeURIComponent(code)}/validate`,
      { method: "GET", headers: config() }
    );
    return unwrapJson<ValidateSignupKeyResponse>(response);
  },

  async refresh(body: RefreshRequest): Promise<RefreshResponse> {
    const response = await fetch(`${url}/v1/auth/refresh`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<RefreshResponse>(response);
  },

  async me(): Promise<MeResponse> {
    const response = await fetch(`${url}/v1/auth/me`, {
      method: "GET",
      headers: config(),
    });
    return unwrapJson<MeResponse>(response);
  },

  async logout(): Promise<void> {
    const response = await fetch(`${url}/v1/auth/logout`, {
      method: "POST",
      headers: config(),
    });
    if (!response.ok && response.status !== 204) {
      await unwrapJson(response);
    }
  },

  async switchWorkspace(body: SwitchWorkspaceRequest): Promise<RefreshResponse> {
    const response = await fetch(`${url}/v1/auth/switch-workspace`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<RefreshResponse>(response);
  },

  /**
   * Triggers the password-reset email flow for the caller's own
   * email. The backend always returns 202 regardless of whether
   * the email is registered — anti-enumeration. The Settings
   * profile tab uses this as the "ubah password" affordance
   * because no direct change-password endpoint exists.
   */
  async forgotPassword(body: { email: string }): Promise<void> {
    const response = await fetch(`${url}/v1/auth/forgot-password`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    if (!response.ok && response.status !== 202) {
      await unwrapJson(response);
    }
  },
});

export default Auth;
